import { useCallback, useEffect, useRef, useState } from 'react';

import events from '~/modules/game-events/game-events';
import { useEventEffect, useEventListenerSelector } from '~/modules/game-events/hooks';
import { PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import { profileOnSlot } from '~/modules/user-profiles/assign';
import { fetchProfileStats, fetchStatsForNames, ProfileStats } from '~/modules/user-profiles/profile-stats';
import UserProfiles, { UserProfile } from '~/modules/user-profiles/profiles';

export const useProfiles = (): UserProfile[] => {
  const [profiles, setProfiles] = useState(UserProfiles.getAll);

  useEffect(() => UserProfiles.addListener(setProfiles), []);

  return profiles;
};

export const useProfile = (id: string | undefined): UserProfile | undefined =>
  useProfiles().find((profile) => profile.id === id);

/**
 * A profile's stats, derived from `SongStats` on demand. Refetches when a play is recorded, so the
 * numbers move as soon as a song ends.
 */
export const useProfileStats = (name: string | undefined): ProfileStats | null => {
  const [stats, setStats] = useState<ProfileStats | null>(null);
  // Which read is the current one. A flag returned from `load` only covered the effect, because
  // `useEventEffect` does not call what its effect returns - so two songs landing close together
  // raced, and whichever read finished last won regardless of which was asked for last.
  const generation = useRef(0);

  const load = useCallback(() => {
    const run = ++generation.current;
    if (!name) {
      setStats(null);
      return;
    }

    void fetchProfileStats(name).then((loaded) => {
      if (run === generation.current) setStats(loaded);
    });
  }, [name]);

  // Re-running counts as a new run in its own right, since `load` claims the next one - so a read
  // still in flight for the profile navigated away from can no longer land on this one.
  useEffect(() => load(), [load]);
  useEventEffect(events.songStatStored, load, [load]);

  return stats;
};

/** Stats for many profiles in one pass over `SongStats`, for the profiles list. */
export const useProfilesStats = (names: string[]): Record<string, ProfileStats> | null => {
  const [stats, setStats] = useState<Record<string, ProfileStats> | null>(null);
  // Joined so the effect keys off the names themselves rather than a new array each render.
  const key = names.join('\n');

  const generation = useRef(0);

  const load = useCallback(() => {
    const run = ++generation.current;

    void fetchStatsForNames(key === '' ? [] : key.split('\n')).then((loaded) => {
      if (run === generation.current) setStats(loaded);
    });
  }, [key]);

  useEffect(() => load(), [load]);
  useEventEffect(events.songStatStored, load, [load]);

  return stats;
};

/**
 * The profile sitting on a player slot, which is simply the profile whose name the slot carries -
 * assignment *is* the name, so there is no separate mapping to keep in step.
 */
export const usePlayerProfile = (playerNumber: PlayerNumber): UserProfile | undefined => {
  const profiles = useProfiles();
  const name = useEventListenerSelector([events.playerNameChanged, events.playerInputChanged], () =>
    PlayersManager.getPlayer(playerNumber)?.getName(),
  );

  return profileOnSlot(profiles, name);
};
